import { DinnerEventOption, Event, EventTypes, TicketCategory } from '../documents/restaurants/event';
import {
  canonicalVatRate,
  hasVatComponents,
  toVatCents,
  validateVatComponents,
  VATComponent,
} from './eventVatComponents';

type CategoryEvent = Pick<Event, 'ticketCategories'> | null | undefined;

/** True when the event sells its occasions at category prices. */
export function hasTicketCategories(event: CategoryEvent): boolean {
  return Array.isArray(event?.ticketCategories) && event.ticketCategories.length > 0;
}

/** The bookable category of an occasion, or null. */
export function ticketCategoryFor(
  event: CategoryEvent,
  occassion: string | null | undefined
): TicketCategory | null {
  if (!occassion || !hasTicketCategories(event)) return null;
  return (
    event.ticketCategories.find(
      (category) => category.occassion === occassion && category.active !== false
    ) ?? null
  );
}

const pickById = <T extends { id: string }>(items: T[] | undefined, ids?: string[] | null) =>
  !items || ids == null ? items : items.filter((item) => ids.includes(item.id));

/** Title suffix of the surcharge part next to the category's own split. */
const SURCHARGE_LABEL = 'Aufpreis';

/**
 * The full split of one option in a category event: the category's split of
 * its price plus the option's surcharge (its own split, or one part at its
 * rate). Null keeps the single-rate path (no split anywhere).
 */
function composedVatComponents(
  category: TicketCategory,
  option: DinnerEventOption
): VATComponent[] | null {
  const surchargeCents = toVatCents(option.amount ?? 0);
  const optionRate = canonicalVatRate(option.vatRate) ?? option.vatRate;
  const categorySplit = hasVatComponents(category);
  if (!categorySplit && !hasVatComponents(option)) return null;

  const base: VATComponent[] = categorySplit
    ? category.vatComponents.map((component) => ({ ...component }))
    : [{ id: 'ticket', title: 'Ticket', grossAmountCents: toVatCents(category.amount), vatRate: optionRate }];
  const surcharge: VATComponent[] = hasVatComponents(option)
    ? option.vatComponents.map((component) => ({
        ...component,
        id: `aufpreis-${component.id}`,
        title: `${component.title} (${SURCHARGE_LABEL})`,
      }))
    : surchargeCents > 0
      ? [{ id: 'aufpreis', title: SURCHARGE_LABEL, grossAmountCents: surchargeCents, vatRate: optionRate }]
      : [];
  return [...base, ...surcharge];
}

/**
 * The event as it sells one category: same id, the category's price, only its
 * options and add-ons, each option with its composed tax split.
 */
export function resolveEventForCategory<E extends Event>(event: E, category: TicketCategory): E {
  const { ticketCategories, ...rest } = event;
  const resolved = {
    ...rest,
    amount: category.amount,
    ticketCategory: {
      id: category.id,
      occassion: category.occassion,
      title: category.title ?? null,
    },
  } as E;
  const options = pickById(event.options, category.optionIds);
  const optionalAddOns = pickById(event.optionalAddOns, category.addOnIds);
  if (options) {
    resolved.options = options.map((option) => {
      const vatComponents = composedVatComponents(category, option);
      return vatComponents ? { ...option, vatComponents } : option;
    });
  }
  if (optionalAddOns) resolved.optionalAddOns = optionalAddOns;
  return resolved;
}

/**
 * The event as the guest of one occasion buys it (see resolveEventForCategory).
 * Events without categories come back unchanged. A category event without a
 * bookable category for the occasion gives null: nothing is sold, there is no
 * fallback to another price.
 */
export function resolveEventForOccassion<E extends Event>(
  event: E | null | undefined,
  occassion: string | null | undefined
): E | null {
  if (!event) return null;
  if (!hasTicketCategories(event)) return event;
  const category = ticketCategoryFor(event, occassion);
  return category ? resolveEventForCategory(event, category) : null;
}

/** Every category (also paused ones) with the event as it would sell it. */
export function ticketCategoryProjections<E extends Event>(event: E | null | undefined) {
  if (!event || !hasTicketCategories(event)) return [];
  return event.ticketCategories.map((category) => ({ category, event: resolveEventForCategory(event, category) }));
}

/** A category event that was not resolved for an occasion must never price a ticket. */
export function isUnresolvedCategoryEvent(event: CategoryEvent): boolean {
  return hasTicketCategories(event);
}

/** Lowest active category price, shown as "from" where no occasion is known. */
export function lowestTicketCategoryAmount(event: CategoryEvent): number | null {
  if (!hasTicketCategories(event)) return null;
  const amounts = event.ticketCategories
    .filter((category) => category.active !== false)
    .map((category) => category.amount)
    .filter((amount) => Number.isFinite(amount));
  return amounts.length ? Math.min(...amounts) : null;
}

/**
 * Stored form of an event: an empty list is the same as no categories, and
 * `amount` holds the lowest category price for readers that do not resolve.
 */
export function normalizeTicketCategoryEvent<E extends Event>(event: E): E {
  if (!event || !Array.isArray(event.ticketCategories)) return event;
  if (!event.ticketCategories.length) {
    const { ticketCategories, ...rest } = event;
    return rest as E;
  }
  const lowest = lowestTicketCategoryAmount(event);
  return lowest === null ? event : { ...event, amount: lowest };
}

/** Structure and sum of a split, without the country catalog (checked on the server). */
const splitMisses = (components: VATComponent[] | null | undefined, euros: number | null | undefined) =>
  validateVatComponents(components, toVatCents(euros ?? 0), null, { frozen: true }).length > 0;

/**
 * Configuration errors of a category event on a shift (German, shown to the
 * restaurant). `shiftOccassions` are the occasions the shift offers.
 */
export function ticketCategoryErrors(
  event: Event | null | undefined,
  shiftOccassions: string[] | null | undefined
): string[] {
  if (!event || !hasTicketCategories(event)) return [];
  const errors: string[] = [];
  const categories = event.ticketCategories;
  const excluded = event.rules?.excludeOccassions ?? [];
  const optionIds = new Set((event.options ?? []).map((option) => option.id));
  const addOnIds = new Set((event.optionalAddOns ?? []).map((addOn) => addOn.id));

  if (event.type !== EventTypes.DINNER_TICKET) {
    errors.push('Preiskategorien gibt es vorerst nur für Dinner-Tickets.');
  }
  // In a category event an option's split covers only its surcharge.
  for (const option of event.options ?? []) {
    if (hasVatComponents(option) && splitMisses(option.vatComponents, option.amount)) {
      errors.push(
        `Die MwSt-Aufteilung der Option „${option.internalName || option.id}“ muss genau ihren Aufpreis ergeben; den Ticketpreis teilt jede Preiskategorie selbst auf.`
      );
    }
  }

  const seenIds = new Set<string>();
  const seenOccassions = new Set<string>();
  for (const category of categories) {
    if (!category?.id) {
      errors.push('Eine Preiskategorie hat keine ID.');
      continue;
    }
    if (seenIds.has(category.id)) errors.push(`Die Kategorie-ID „${category.id}“ ist doppelt.`);
    seenIds.add(category.id);

    if (!category.occassion) {
      errors.push(`Die Kategorie „${category.id}“ hat keinen Anlass.`);
      continue;
    }
    if (seenOccassions.has(category.occassion)) {
      errors.push(`Der Anlass „${category.occassion}“ hat mehr als eine Preiskategorie.`);
    }
    seenOccassions.add(category.occassion);

    if (typeof category.amount !== 'number' || !Number.isFinite(category.amount) || category.amount < 0) {
      errors.push(`Die Kategorie für „${category.occassion}“ braucht einen Preis ab 0.`);
    }
    if (hasVatComponents(category) && splitMisses(category.vatComponents, category.amount)) {
      errors.push(`Die MwSt-Aufteilung der Kategorie für „${category.occassion}“ ergibt nicht ihren Ticketpreis.`);
    }
    if (shiftOccassions && !shiftOccassions.includes(category.occassion)) {
      errors.push(`Der Anlass „${category.occassion}“ ist der Schicht nicht zugeordnet.`);
    }
    if (excluded.includes(category.occassion)) {
      errors.push(`Der Anlass „${category.occassion}“ ist vom Ticket ausgeschlossen und kann keine Preiskategorie haben.`);
    }
    if (Array.isArray(category.optionIds)) {
      if (!category.optionIds.length) {
        errors.push(`Die Kategorie für „${category.occassion}“ braucht mindestens eine Option.`);
      }
      category.optionIds
        .filter((id) => !optionIds.has(id))
        .forEach((id) => errors.push(`Die Option „${id}“ der Kategorie für „${category.occassion}“ gibt es nicht.`));
    }
    (category.addOnIds ?? [])
      .filter((id) => !addOnIds.has(id))
      .forEach((id) => errors.push(`Die Zusatzleistung „${id}“ der Kategorie für „${category.occassion}“ gibt es nicht.`));
  }

  (shiftOccassions ?? [])
    .filter((occassion) => !seenOccassions.has(occassion) && !excluded.includes(occassion))
    .forEach((occassion) =>
      errors.push(
        `Der Anlass „${occassion}“ braucht eine Preiskategorie oder muss vom Ticket ausgeschlossen werden.`
      )
    );

  return errors;
}
